import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  loadOverrideSchedule,
  loadSchedule,
  saveOverrideSchedule,
  saveSchedule,
} from "../service/DynamoService";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import { AttributeValue } from "@aws-sdk/client-dynamodb";
import isoWeek from "dayjs/plugin/isoWeek";
import isoWeeksInYear from "dayjs/plugin/isoWeeksInYear";
import isLeapYear from "dayjs/plugin/isLeapYear";
import { DAYS, ScheduleGrid } from "../components/ScheduleGrid";

dayjs.extend(isoWeek);
dayjs.extend(isLeapYear);
dayjs.extend(isoWeeksInYear);
dayjs.locale("de");

// next 52 weeks
const WEEK_OPTIONS = Array.from({ length: 52 }, (_, idx) => {
  const m = dayjs().startOf("isoWeek").add(idx, "week");
  const weekNum = m.isoWeek();
  const weekYear = m.isoWeekYear();
  const weekStart = m.startOf("isoWeek");
  const weekEnd = m.endOf("isoWeek");
  return {
    weekNum,
    weekYear,
    label: `KW ${weekNum}/${weekYear} (${weekStart.format("DD.MM.YYYY")} – ${weekEnd.format("DD.MM.YYYY")})`,
  };
});

interface DailySchedule {
  day: string;
  open: Dayjs;
  close: Dayjs;
  closed: boolean;
  date?: Dayjs;
}

/**
 * Create an empty 9–17 schedule for all days.
 */
function makeEmptySchedule(): DailySchedule[] {
  return DAYS.map((day) => ({
    day,
    open: dayjs().hour(9).minute(0),
    close: dayjs().hour(17).minute(0),
    closed: true,
  }));
}

/**
 * Overlay DynamoDB items onto a base schedule.
 */
function applyItemsToSchedule(
  items: Record<string, AttributeValue>[],
  baseSchedule: DailySchedule[],
  prefix: string,
  dateResolver?: (day: string, idx: number) => Dayjs,
): DailySchedule[] {
  return baseSchedule.map((slot, idx) => {
    const date = dateResolver?.(slot.day, idx);
    const match = items.find((i) => i.SK.S === `${prefix}${slot.day}`);
    if (match) {
      return {
        day: slot.day,
        date,
        open: dayjs(match.open.S, "HH:mm"),
        close: dayjs(match.close.S, "HH:mm"),
        closed: match.closed.BOOL!,
      };
    }
    // no match: return base slot, with date if provided
    return date ? { ...slot, date } : { ...slot };
  });
}

/**
 * Serialize a DailySchedule[] to the DynamoDB write payload.
 */
function serializeSchedule(weekly: DailySchedule[]) {
  return weekly.map(({ day, date, open, close, closed }) => {
    const item: any = {
      day,
      open: open.format("HH:mm"),
      close: close.format("HH:mm"),
      closed,
    };
    if (date) {
      item.date = date.format("YYYY-MM-DD");
    }
    return item;
  });
}

export function OpeningHours() {
  const [isDirty, setIsDirty] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<number>(
    WEEK_OPTIONS[0].weekNum,
  );
  // 0 = Standard, 1 = Sonder-Öffnungszeiten
  const [tab, setTab] = useState<number>(0);
  // store global (default) weekly schedule
  const [globalWeekly, setGlobalWeekly] =
    useState<DailySchedule[]>(makeEmptySchedule());
  // actual displayed weekly, either global or override
  const [weekly, setWeekly] = useState<DailySchedule[]>(makeEmptySchedule());

  useEffect(() => {
    // Only load global schedule when overrides are NOT in use
    if (tab === 1) return;
    (async () => {
      try {
        const items = await loadSchedule();
        const newGlobal = applyItemsToSchedule(
          items,
          makeEmptySchedule(),
          "SCHEDULE#",
        );
        setGlobalWeekly(newGlobal);
        setWeekly(newGlobal);
        setIsDirty(false);
      } catch (err) {
        console.error("DynamoDB query error", err);
      }
    })();
  }, [tab]);

  useEffect(() => {
    if (tab !== 1 || selectedWeek == null) return;
    (async () => {
      try {
        const weekStart = dayjs().isoWeek(selectedWeek).startOf("isoWeek");
        const weekYear = weekStart.year();
        const prefix = `OVERRIDE#${weekYear}-KW${selectedWeek}#`;
        const items = await loadOverrideSchedule(selectedWeek, weekYear);
        const newWeekly = applyItemsToSchedule(
          items,
          globalWeekly,
          prefix,
          (_day, idx) => weekStart.add(idx, "day"),
        );
        setWeekly(newWeekly);
      } catch (err) {
        console.error("Override load error", err);
      }
    })();
  }, [tab, selectedWeek, globalWeekly]);

  const handleWeeklyChange = (
    index: number,
    field: keyof Omit<DailySchedule, "day">,
    value: any,
  ) => {
    setWeekly((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    const weekStart = dayjs().isoWeek(selectedWeek).startOf("isoWeek");
    const weekYear = weekStart.year();
    const payload = serializeSchedule(weekly);
    try {
      if (tab === 1) {
        await saveOverrideSchedule(selectedWeek, weekYear, payload);
      } else {
        await saveSchedule(payload);
      }
      console.log(tab === 1 ? "Override gespeichert" : "Global gespeichert");
      setIsDirty(false);
    } catch (err) {
      console.error("DynamoDB write error", err);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h4" sx={{ wordBreak: "break-word" }}>
        Öffnungszeitenverwaltung
      </Typography>

      <Button variant="contained" disabled={!isDirty} onClick={handleSave}>
        Änderungen speichern
      </Button>

      <Tabs value={tab} onChange={(_, newVal) => setTab(newVal)} sx={{ mb: 2 }}>
        <Tab label="Standard-Öffnungszeiten" />
        <Tab label="Sonder-Öffnungszeiten" />
      </Tabs>

      {tab === 1 && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="week-select-label">Kalenderwoche</InputLabel>
            <Select
              labelId="week-select-label"
              label="Kalenderwoche"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value as number)}
            >
              {WEEK_OPTIONS.map(({ weekNum, label }) => (
                <MenuItem key={weekNum} value={weekNum}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Zeiten nur gültig für die oben ausgewählte Kalenderwoche.
          </Typography>
        </>
      )}

      <ScheduleGrid
        onWeeklyChange={handleWeeklyChange}
        switchLabel="Geöffnet"
        weekly={weekly}
      />
    </Stack>
  );
}
